import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Grid,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { createUserWithBusiness, fetchOwnedBusinesses } from '../services/userManagementService';
import BusinessSelector from './BusinessSelector';
import BusinessFormFields from './BusinessFormFields';

// Valid role constants
//TODO: this should probably be pulling from a constant file that has the roles
//TODO: for beta only internal pm and internal trade are available
// INTERNAL_ADMIN is conditionally added based on current user's role

const BASE_INTERNAL_ROLES = ['INTERNAL_PM', 'INTERNAL_SUPER', 'INTERNAL_TRADE'];
const EXTERNAL_ROLES = [
    'EXTERNAL_OWNER',
    'EXTERNAL_CONSULTANT',
    'EXTERNAL_SUBCONTRACTOR_PM',
    'EXTERNAL_SUBCONTRACTOR_TRADE',
];

// The backend returns errors as a JSON object ({ status, error, message, path,
// timestamp }), so err.response.data is never a string. Passing it to setError()
// renders an object as a React child, which throws and blanks the whole page.
const getErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    return fallback;
};

const INITIAL_FORM_STATE = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    accountStatus: 'ACTIVE',
    selectedBusiness: '',
    role: '',
    isCreatingNewBusiness: false,
    newBusiness: {
        businessName: '',
        email: '',
        phoneNumber: '',
        streetAddress: '',
        city: '',
        province: '',
        postalCode: '',
        country: '',
    },
};

/**
 * CreateUserDialog Component
 * Implements user creation with business assignment and role
 * @param {string} userType - 'internal' or 'external' to determine available options
 * @param {string} currentUserRole - Current user's role to determine if INTERNAL_ADMIN option should be shown
 */
const CreateUserDialog = ({
    open,
    onClose,
    onUserCreated,
    userType = 'internal',
    currentUserRole = null,
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [businesses, setBusinesses] = useState([]);
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState({});

    const resetForm = () => {
        setFormData(INITIAL_FORM_STATE);
        setFormErrors({});
        setError(null);
        setLoading(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Load businesses when dialog opens
    useEffect(() => {
        if (open) {
            loadBusinesses();
        }
    }, [open]);

    const loadBusinesses = async () => {
        try {
            // Automatically scoped to logged-in user's business - no businessId needed
            const businessData = await fetchOwnedBusinesses();
            setBusinesses(businessData);
        } catch (err) {
            console.error('Error loading businesses:', err);
            setError('Failed to load businesses.');
        }
    };

    const validateForm = () => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!formData.email.trim()) errors.email = 'Email is required';
        else if (!emailRegex.test(formData.email)) errors.email = 'Invalid email format';
        if (!formData.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';

        // Business validation - only for external users
        if (userType === 'external') {
            if (formData.isCreatingNewBusiness) {
                const biz = formData.newBusiness;
                const bizErrors = {};
                if (!biz.businessName.trim()) bizErrors.businessName = 'Business name is required';
                if (!biz.email.trim()) bizErrors.email = 'Business email is required';
                else if (!emailRegex.test(biz.email)) bizErrors.email = 'Invalid email format';
                if (!biz.phoneNumber.trim()) bizErrors.phoneNumber = 'Business phone is required';
                if (!biz.streetAddress.trim())
                    bizErrors.streetAddress = 'Business address is required';
                if (!biz.city.trim()) bizErrors.city = 'City is required';
                if (!biz.province.trim()) bizErrors.province = 'Province is required';
                if (!biz.postalCode.trim()) bizErrors.postalCode = 'Postal code is required';
                if (!biz.country.trim()) bizErrors.country = 'Country is required';
                if (Object.keys(bizErrors).length > 0) errors.newBusiness = bizErrors;
            } else {
                if (!formData.selectedBusiness)
                    errors.selectedBusiness = 'Please select a business';
            }
        }

        if (!formData.role) errors.role = 'Please select a role';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            setError(null);

            let businessData;

            if (userType === 'internal') {
                // Internal users automatically use the current user's internal business
                const internalBusiness = businesses.find((b) => b.subscriptionType !== null);
                businessData = {
                    businessId: internalBusiness?.businessId,
                    businessName: internalBusiness?.businessName || '',
                };
            } else if (formData.isCreatingNewBusiness) {
                const biz = formData.newBusiness;
                businessData = {
                    businessName: biz.businessName.trim(),
                    email: biz.email.trim(),
                    phoneNumber: cleanPhoneNumber(biz.phoneNumber),
                    streetAddress: biz.streetAddress.trim(),
                    city: biz.city.trim(),
                    province: biz.province.trim(),
                    postalCode: biz.postalCode.trim(),
                    country: biz.country.trim(),
                    subscriptionType: null,
                };
            } else {
                // Using existing external business
                const selectedBiz = businesses.find(
                    (b) => b.businessId === formData.selectedBusiness
                );
                businessData = {
                    businessId: selectedBiz?.businessId,
                    businessName: selectedBiz?.businessName || '',
                };
            }

            const payload = {
                user: {
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    email: formData.email.trim(),
                    phoneNumber: cleanPhoneNumber(formData.phoneNumber),
                    accountStatus: formData.accountStatus,
                },
                business: businessData,
                role: formData.role,
            };

            await createUserWithBusiness(payload);
            onUserCreated();
            handleClose();
        } catch (err) {
            console.error('Error creating user:', err);

            // Handle specific error responses from backend
            if (err.response?.status === 409) {
                // 409 Conflict - Email or phone already exists
                setError(
                    getErrorMessage(err, 'Email or phone number already exists in the system.')
                );
            } else if (err.response?.status === 400) {
                // 400 Bad Request - Validation errors
                setError(getErrorMessage(err, 'Invalid data provided. Please check your inputs.'));
            } else {
                // Generic error fallback
                setError(getErrorMessage(err, 'User creation failed. Please try again.'));
            }
        } finally {
            setLoading(false);
        }
    };

    const getAvailableRoles = () => {
        if (userType === 'internal') {
            // Only show INTERNAL_ADMIN option if current user is an admin
            if (currentUserRole === 'INTERNAL_ADMIN') {
                return ['INTERNAL_ADMIN', ...BASE_INTERNAL_ROLES];
            }
            return BASE_INTERNAL_ROLES;
        } else {
            return EXTERNAL_ROLES;
        }
    };

    const formatRoleDisplay = (role) => {
        return role
            .replace(/[_]/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const cleanPhoneNumber = (phoneNumber) => {
        // Remove all non-digit characters for backend storage
        return phoneNumber.replace(/[^0-9]/g, '');
    };

    const handlePhoneChange = (e, fieldName) => {
        const value = e.target.value;
        // Only allow numbers
        const numbersOnly = /^[0-9]*$/;
        if (!numbersOnly.test(value)) {
            return; // Don't update if contains non-numeric characters
        }

        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };

    const handleNewBusinessFieldChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            newBusiness: { ...prev.newBusiness, [field]: value },
        }));
    };

    const handleNewBusinessPhoneChange = (field, value) => {
        const numbersOnly = /^[0-9]*$/;
        if (!numbersOnly.test(value)) return;
        handleNewBusinessFieldChange(field, value);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Typography variant="h6">
                    Add New {userType === 'internal' ? 'Internal' : 'External'} User
                </Typography>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={formData.firstName}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                            }
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={formData.lastName}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                            }
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={formData.email}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, email: e.target.value }))
                            }
                            error={!!formErrors.email}
                            helperText={formErrors.email}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={formData.phoneNumber}
                            onChange={(e) => handlePhoneChange(e, 'phoneNumber')}
                            error={!!formErrors.phoneNumber}
                            helperText={formErrors.phoneNumber}
                            placeholder="(123) 456-7890"
                            required
                        />
                    </Grid>
                    {/* Business Selection - Only for External Users */}
                    {userType === 'external' && (
                        <Grid item xs={12}>
                            <BusinessSelector
                                businesses={businesses}
                                filter={(b) => b.subscriptionType === null}
                                value={{
                                    businessId: formData.selectedBusiness || null,
                                    isCreatingNew: formData.isCreatingNewBusiness,
                                }}
                                onChange={({ businessId, isCreatingNew }) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        selectedBusiness: businessId || '',
                                        isCreatingNewBusiness: isCreatingNew,
                                        role: '',
                                    }))
                                }
                                required
                                error={!!formErrors.selectedBusiness}
                                helperText={formErrors.selectedBusiness}
                            />
                        </Grid>
                    )}

                    {/* New Business Fields - Show when creating new business */}
                    {formData.isCreatingNewBusiness && (
                        <>
                            <Grid item xs={12}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 2, color: '#374151', fontWeight: 600 }}
                                >
                                    New Business Information
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <BusinessFormFields
                                    values={formData.newBusiness}
                                    onChange={handleNewBusinessFieldChange}
                                    onPhoneChange={handleNewBusinessPhoneChange}
                                    errors={formErrors.newBusiness || {}}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <FormControl
                            fullWidth
                            error={!!formErrors.role}
                            disabled={
                                userType === 'internal'
                                    ? false
                                    : !formData.selectedBusiness && !formData.isCreatingNewBusiness
                            }
                        >
                            <InputLabel>Select Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Select Role"
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, role: e.target.value }))
                                }
                            >
                                {getAvailableRoles().map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {formatRoleDisplay(role)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? 'Creating...' : 'Create User'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateUserDialog;
